import clsx from 'clsx'
import { replace } from 'connected-react-router'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ConnectedProps } from 'react-redux'
import { connect } from 'react-redux'
import type { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router'
import styled from 'styled-components'

import type { SearchFormRef } from 'components/Search'
import { mediaQueryUp } from 'components/utils/breakpoint'
import {
  ALL_HOTEL_TYPES,
  OFFER_TYPE_HOTEL,
} from 'constants/offer'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import {
  decodeSearchParams,
  getDistanceEqFilter,
  getDistanceLteFilter,
  getSearchTypeFromFilters,
} from 'lib/search/searchUtils'
import {
  deleteSearchParams,
  parseSearchString,
} from 'lib/url/searchUrlUtils'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

import type { RankingPreferences } from '@luxuryescapes/contract-search'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import { addToMetasearchBlacklist } from 'actions/MarketingAction'
import { fetchMetaSearchOfferRates } from 'actions/OfferActions'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { getListName } from 'analytics/enhanced-ecommerce'
import { impressionEventWithContext, productClickEventWithContext, searchEvent } from 'analytics/snowplow/events'
import { CompactFooter, OriginalFooter } from 'components/App/Footer'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import HttpStatus from 'components/Common/HttpStatus'
import LayoutContainer from 'components/Common/LayoutContainer'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import OfferListLoadMoreButton from 'components/OfferList/OfferListLoadMoreButton'
import OfferListLoadingOfferTile from 'components/OfferList/OfferListTiles/OfferListLoadingOfferTile'
import OfferListOfferTile from 'components/OfferList/OfferListTiles/OfferListOfferTile'
import type {
  FilterControlChangeHandler,
} from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import MetaSearchPageHeadData from 'components/Pages/HotelSearchPage/MetaSearchPageHeadData'
import MetaSearchPageWarmupHints from 'components/Pages/HotelSearchPage/MetaSearchPageWarmupHints'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import config from 'constants/config'
import { Experiments, OptimizelyExperiments } from 'constants/optimizely'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { MetaSearchContext } from 'contexts/MetasearchContext'
import useMetaSearchOfferList from 'hooks/Offers/useMetaSearchOfferList'
import useOffer from 'hooks/Offers/useOffer'
import useOfferList from 'hooks/Offers/useOfferList'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import useExperiment from 'hooks/Optimizely/useExperiment'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import useQueryParams from 'hooks/useQueryParams'
import { buildMetaSearchObjectFromOffer, buildMetaSearchOfferListFilters } from 'lib/metasearch/metaSearchOfferListFilters'
import getOfferListKey from 'lib/offer/offerListKey'
import { isBedbankOffer, isHotel } from 'lib/offer/offerTypes'
import { generalLog } from 'marketing/api'
import VoucherDisplayConfig from 'marketing/components/VoucherDisplayConfig'
import { useIntl } from 'react-intl'
import { getSourceForProperty } from 'selectors/offerSelectors'
import Group from '../../utils/Group'
import Error404PageWithOffers from '../Error404PageWithOffers/Error404PageWithOffers'
import HotelSearchHeader from './HotelSearchHeader'

const YMAL_LIMIT = 20
const MAIN_OFFER_LIMIT = 1
const DISTANCE_PRIORITY_TILE_COUNT = 4
const DISTANCE_LOAD_MORE_BATCH_SIZE = 4
const YMAL_PRIORITY_TILE_COUNT = 4
const METASEARCH_DISTANCE_FUNCTION_SCORE_WEIGHTS: RankingPreferences = {
  distanceImportance: 1,
  offerScoreImportance: 0,
  offsetMeters: 0,
}

// Determine default sort mode based on region code
const REGION_SORT_MAP: Record<string, string> = {
  us: 'distance',
}
function getSortMode(regionCode: string) {
  if (config.BRAND === 'luxuryescapes') {
    return REGION_SORT_MAP[regionCode.toLowerCase()] || 'recommendation'
  }
  return 'recommendation'
}

const PageWrapper = styled(Group)`
  height: calc(var(--vh, 1vh) * 100);
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    display: grid;
    padding-top: ${rem(24)};
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 2fr);
      padding-top: ${rem(12)};
    }
  }

  &.fullWidth {
    padding: ${rem(16)} 0;
  }
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
  margin-top: ${rem(16)};
`

function mapState(state: App.State) {
  const filters = getUrlOfferListFilters(state)

  return {
    windowSearch: state.router.location.search,
    pathName: state.router.location.pathname,
    filters,
    memberId: state.auth.account.memberId,
    searchResultMetadata: state.offer.searchResultMetadata,
    isFlexibleSearch: !!(filters?.flexibleMonths && filters?.flexibleNights),
    specificPlaces: state.destination.specificPlaces[filters.destinationId ?? ''],
    region: state.geo.currentRegionCode,
  }
}

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface Props extends PropsFromRedux, RouteComponentProps<any> {}

function MetaSearchPage({
  filters,
  searchResultMetadata,
  windowSearch,
  pathName,
  history,
  match,
  region,
}: Props) {
  const intl = useIntl()
  const { landmarkSearchSortByTypes, searchSortByTypes, SORT_OPTION_CUSTOMER_RATING } = useOfferListSortOptions()
  const queryParams = useQueryParams()
  const propertyId = match?.params?.propertyId
  const dispatch = useAppDispatch()
  const isMobile = useScreenSizeOnly('mobile')
  const searchType = getSearchTypeFromFilters(filters)
  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const searchParams = useMemo(() => decodeSearchParams(windowSearch), [windowSearch])
  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const utmBp = useAppSelector(state => state.utm.bp)
  const utmContent = useAppSelector(state => state.utm.content)
  const utmSource = useAppSelector(state => state.utm.source)
  const utmMedium = useAppSelector(state => state.utm.medium)
  const topOfferSource = useAppSelector(state => getSourceForProperty(state, propertyId))
  const flashOfferId = useAppSelector(state =>
    state.offer.alternativeOffersForProperty[propertyId]?.leOfferDetails.find(
      detail => detail.type === OFFER_TYPE_HOTEL,
    )?.offerId,
  )
  const { isAnyVariant: isCugExperimentOn } = useExperiment(
    Experiments.growthMetasearchCugSecondaryOffers,
  )

  // Blacklist this property so it retains B2C rates if the user navigates to its /partner/ page
  // or encounters it on search results later within the TTL window
  useEffect(() => {
    if (isCugExperimentOn && utmBp === '3' && propertyId) {
      dispatch(addToMetasearchBlacklist(propertyId))
    }
  }, [dispatch, isCugExperimentOn, utmBp, propertyId])
  const userAgent = useAppSelector(state => state.system.rawUserAgentString)
  const isGoogleHotelAdsVerifier = userAgent.includes('Google-HotelAdsVerifier')
  const listSortMode = isGoogleHotelAdsVerifier
    ? 'recommendation'
    : (queryParams.get('sortBy') as 'recommendation' | 'distance' | 'discount' | 'rating') ||
      getSortMode(region)
  const {
    searchTargetLabel,
    offerDistanceFromSearchTarget,
  } = useMemo(() => ({
    searchTargetLabel: params.landmarkName || params.destinationName || params.propertyName,
    offerDistanceFromSearchTarget: searchResultMetadata.distanceFromSearchTarget[params.landmarkId || params.destinationId || ''],
  }), [
    params.landmarkId,
    params.landmarkName,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    searchResultMetadata.distanceFromSearchTarget,
  ])

  const formRef = useRef<SearchFormRef>(null)
  const hideSearchHeader = queryParams.get('hide_search') === 'true'
  const shouldShowPerNightPricing = useOptimizelyExperiment(OptimizelyExperiments.growthMetasearchPerNightPricing, config.BRAND === 'luxuryescapes' && (utmContent === 'gha-lpp' || (utmSource === 'google' && utmMedium === 'cpc')))
  const luxuryHotelFallbackName = intl.formatMessage({
    id: 'HKJViPBpFCTQAQ6DQ151',
    defaultMessage: 'Luxury Hotel',
    description: 'Fallback property name used on the meta search page when a hotel offer name is unavailable or not a hotel',
  })

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    searchTargetLabel,
    offerDistanceFromSearchTarget,
  }, queryParams)

  const listFilters = useMemo((): App.OfferListFilters => {
    return {
      ...filters,
      offerTypes: [...ALL_HOTEL_TYPES],
      distanceEq: getDistanceEqFilter(searchType),
      distanceLte: getDistanceLteFilter(searchType),
      source: topOfferSource,
      propertyId,
      limit: MAIN_OFFER_LIMIT,
    }
  }, [filters, searchType, topOfferSource, propertyId])

  const [offer, fetching] = useOffer(propertyId, {
    requireSummaryOnly: true,
  })

  useEffect(() => {
    const nextQueryParams = new URLSearchParams(queryParams)
    const childrenQueryParam = queryParams.get('children')
    const adultQueryParam = queryParams.get('adults')
    const propertyNameQueryParam = queryParams.get('propertyName')
    const propertyIdPrefix = isBedbankOffer(offer) ? 'bedbank' : 'le'
    const currentSortBy = queryParams.get('sortBy')
    if (listSortMode !== currentSortBy && currentSortBy !== null) {
      nextQueryParams.set('sortBy', listSortMode)
    }
    if (!childrenQueryParam?.trim()) {
      nextQueryParams.set('children', 'none')
    }
    if (!adultQueryParam || adultQueryParam === '') {
      nextQueryParams.set('adults', '2')
    }
    nextQueryParams.set('propertyId', `${propertyIdPrefix}:${propertyId}`)
    if (!propertyNameQueryParam) {
      const propertyName = isHotel(offer) ? offer.property?.name : luxuryHotelFallbackName
      nextQueryParams.set('propertyName', propertyName)
    }

    const nextSearch = nextQueryParams.toString()
    const currentSearch = windowSearch.replace(/^\?/, '')

    if (nextSearch === currentSearch) {
      return
    }

    history.replace({
      search: nextSearch,
    })
  }, [history, queryParams, propertyId, offer, listSortMode, luxuryHotelFallbackName, windowSearch])

  const metaSearchFilters = useMemo(
    () => buildMetaSearchObjectFromOffer(offer, region, filters, { limit: YMAL_LIMIT }),
    [offer, region, filters],
  )

  const {
    items: metaSearchOfferList,
    fetching: metaSearchOfferListFetchingFromHook,
  } = useMetaSearchOfferList(metaSearchFilters, {
    disabled: !offer,
    sortMode: listSortMode,
    propertyId,
  })

  const metaSearchOfferListFetching = isBedbankOffer(offer) && (
    fetching || metaSearchOfferListFetchingFromHook
  )

  const isDistanceMode = listSortMode === 'distance'

  const distanceSearchFilters = useMemo((): App.OfferListFilters | undefined => {
    if (!isBedbankOffer(offer) || !propertyId) return undefined
    return {
      propertyId: `bedbank:${propertyId}`,
      isStream: true,
      offerTypes: [...ALL_HOTEL_TYPES],
      checkIn: filters.checkIn,
      checkOut: filters.checkOut,
      rooms: filters.rooms,
      limit: YMAL_LIMIT,
      include: ['nearby'],
    }
  }, [offer, propertyId, filters.checkIn, filters.checkOut, filters.rooms])

  const [distanceMinRequiredOffers, setDistanceMinRequiredOffers] = useState<number>(DISTANCE_PRIORITY_TILE_COUNT)

  useEffect(() => {
    setDistanceMinRequiredOffers(DISTANCE_PRIORITY_TILE_COUNT)
  }, [propertyId, filters.checkIn, filters.checkOut, filters.rooms])

  const onDistanceLoadMore = useCallback(() => {
    setDistanceMinRequiredOffers(previousMinRequiredOffers => (
      Math.min(previousMinRequiredOffers + DISTANCE_LOAD_MORE_BATCH_SIZE, YMAL_LIMIT)
    ))
  }, [])

  const distanceExcludedIds = useMemo(
    () => [offer?.id, flashOfferId].filter((id): id is string => !!id),
    [offer?.id, flashOfferId],
  )

  const distanceOfferList = useOfferList(distanceSearchFilters, {
    disabled: !isDistanceMode || !offer,
    excludedIds: distanceExcludedIds.length > 0 ? distanceExcludedIds : undefined,
    // Start with the first batch and progressively extend while the user scrolls.
    minRequiredOffers: distanceMinRequiredOffers,
    functionScoreWeights: METASEARCH_DISTANCE_FUNCTION_SCORE_WEIGHTS,
  })

  // Warmup-hints offer-list key. Must match the YMAL list key the SSR
  // fetcher writes to (see `fetchMetaSearchOffer.ts`) so the head warmup
  // script reads the same SSR-populated id list (with flash already merged
  // at index 0 via `fetchMetaOfferIds(filters, prependOfferId)`).
  const ymalListKey = useMemo(
    () => metaSearchFilters
      ? getOfferListKey(buildMetaSearchOfferListFilters(metaSearchFilters))
      : undefined,
    [metaSearchFilters],
  )

  const sortOptions = useMemo(() => {
    let options = searchType === 'landmark' ? landmarkSearchSortByTypes : searchSortByTypes

    options = [...options, SORT_OPTION_CUSTOMER_RATING]

    return options
  }, [searchType, SORT_OPTION_CUSTOMER_RATING, landmarkSearchSortByTypes, searchSortByTypes])

  const Footer = config.footer.type === 'original' ? OriginalFooter : CompactFooter

  const [showFooter, setShowFooter] = useState<boolean>(false)

  const mapViewText = intl.formatMessage({
    id: 'zPBWnim4YLg_6QKIaKJg',
    defaultMessage: 'Map view',
    description: 'Button text to switch to map view on the map or location view',
  })

  const saveUpToBannerText = intl.formatMessage({
    id: 'wGlAkHeVO437Q9xej0BW',
    defaultMessage: 'Save up to 58% on these exclusive nearby offers!',
    description: 'Banner message promoting discounts on nearby offers',
  })
  const shouldShowSaveUpToBanner = !flashOfferId

  const recommendedLabel = intl.formatMessage({
    id: 'xCfG-BqN_4hiZ-8TVmBx',
    defaultMessage: 'Recommended',
    description: 'Sort option label for recommended offers on the offer page',
  })

  const closestDistanceLabel = intl.formatMessage({
    id: 'e1S0PCz6_43aKRgLwX7S',
    defaultMessage: 'Closest distance',
    description: 'Sort option label for closest distance on the hotel search page',
  })

  const highestDiscountLabel = intl.formatMessage({
    id: 'culV-HTaN_fmw3wtWjA0',
    defaultMessage: 'Highest discount value',
    description: 'Sort option label for highest discount on the hotel search page',
  })

  const highestRatingLabel = intl.formatMessage({
    id: 'eXCBkesZB5vuChkGyT33',
    defaultMessage: 'Highest rating',
    description: 'Sort option label for highest rating on the hotel search page',
  })

  const metaSearchSortOptions = [
    { label: recommendedLabel, value: 'recommendation' },
    { label: closestDistanceLabel, value: 'distance' },
    { label: highestDiscountLabel, value: 'discount' },
    { label: highestRatingLabel, value: 'rating' },
  ]

  const sortInputs = metaSearchSortOptions.map(option => <FilterChip
    key={option.value}
    selected={listSortMode === option.value}
    onClick={() => {
      const nextParams = new URLSearchParams(queryParams)
      nextParams.set('sortBy', option.value)
      history.replace({ search: nextParams.toString() })
    }}
    name={option.label}
    value={option.value}
    size="medium"
  >
    {option.label}
  </FilterChip>)

  useEffect(() => {
    dispatch(fetchMetaSearchOfferRates({
      propertyId,
      filters: listFilters,
      source: topOfferSource,
      regionCode: region,
      offerListKey: getOfferListKey(listFilters),
      alternativeSourceOfferListKey: getOfferListKey(listFilters),
    }))
  }, [dispatch, listFilters, propertyId, region, topOfferSource])

  useEffect(() => {
    trackClientEvent({
      subject: listSortMode,
      action: 'sort-assigned',
      category: 'metasearch',
      type: 'nonInteraction',
    })
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!fetching && !offer) {
      const paramObject = Object.fromEntries(queryParams.entries())
      generalLog({
        status: 'error',
        message: 'MetaSearchPage: No results found',
        payload: {
          ...paramObject,
          propertyId,
          fullPath: pathName + windowSearch,
        },
      })
    }
  }, [fetching, offer, pathName, propertyId, queryParams, windowSearch])

  useEffect(() => {
    if (!fetching && offer) {
      setShowFooter(true)
    }
  }, [fetching, offer, isMobile])

  // Detect destination changes when search is submitted
  useEffect(() => {
    const hasDestinationChanged =
      !!params.destinationId ||
      !!params.landmarkId ||
      (!!params.propertyId && !params.propertyId.endsWith(propertyId))

    if (hasDestinationChanged) {
      history.replace({
        pathname: `/${region}/search`,
        search: windowSearch,
      })
    }
  }, [
    history,
    region,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    params.propertyId,
    params.landmarkId,
    params.landmarkName,
    propertyId,
    pathName,
    windowSearch,
  ])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'auto', top: 0 })
  }, [dispatch])

  const offerListFiltersProps = {
    sortOptions,
    searchType,
    switchView: {
      label: mapViewText,
      view: 'map' as const,
      URL: mapViewURL,
    },
    listFilters,
  }

  // Initiate tracking for the meta search page
  const tracking = useOfferListTracking(
    getListName('MetaSearch', 'MetaSearch', 'Meta Search Page'),
    {
      snowplow: {
        enabled: false,
      },
      key: getOfferListKey(listFilters),
    },
  )

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    // we only want to fire this event on the main offer list
    // and not the extra offer list to avoid dup events
    if (dispatchAction.type === OfferListEvents.listLoad) {
      trackEvent(searchEvent('offer-list-load', 'hotels-search-page', {
        filters,
        searchItem: searchParams.searchItem,
        checkIn: filters.checkIn,
        checkOut: filters.checkOut,
        vertical: 'hotels',
        occupancies: filters.rooms,
      }))
    }
    if (dispatchAction.type === OfferListEvents.productClick) {
      const { offer, position, price, memberPrice, duration, extendedAvailability, availability } = dispatchAction
      trackEvent(
        productClickEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration },
        ),
      )
      trackClientEvent({
        subject: 'metasearch-offer-clicked',
        action: 'click',
        category: 'logging',
        type: 'interaction',
        optimizelyEventId: '4992635119599616',
        optimizelyEventKey: 'metasearch_offer_click',
      })
      if (extendedAvailability) {
        trackClientEvent({
          subject: 'clicked',
          action: `${extendedAvailability}`,
          category: 'extended-window',
          type: 'interaction',
        })
      }
    } else if (dispatchAction.type === OfferListEvents.productImpression) {
      const { offer, position, availability, price, memberPrice, duration, extendedAvailability, roomRateId } = dispatchAction
      trackEvent(
        impressionEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, roomRateId },
        ),
      )
      if (extendedAvailability) {
        trackClientEvent({
          subject: 'viewed',
          action: `${extendedAvailability}`,
          category: 'extended-window',
          type: 'interaction',
        })
      }
    }
  }, [tracking.listName, tracking.listId, filters, searchParams.searchItem])

  if (!offer && !fetching) {
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }

  const shouldShowDistanceLoadingTiles = isDistanceMode &&
    !!distanceSearchFilters &&
    distanceOfferList.offerIds.length === 0 &&
    (
      distanceOfferList.fetching ||
      (
        distanceOfferList.socketListenersStatus === 'listening' &&
        !distanceOfferList.streamEnded
      )
    )

  // Render the flash tile unconditionally when known so it stays visible
  // through the streamed distance list's loading window — keeps slot 0
  // mounted across the skeleton→stream transition (stable React key).
  const flashDistanceTile = flashOfferId ? (
    <OfferListOfferTile
      key={flashOfferId}
      offerId={flashOfferId}
      position={0}
      eagerLoadImage
      lazyLoad="none"
      tileStyle="search"
      wrapperClass="offer-tile"
      filters={{ showDistanceWithMetaOfferId: propertyId, checkIn: filters.checkIn, checkOut: filters.checkOut }}
      showPerNightPricing={!!shouldShowPerNightPricing}
    />
  ) : null

  const distanceTilesBelow = shouldShowDistanceLoadingTiles
    ? Array.from({ length: DISTANCE_PRIORITY_TILE_COUNT }, (_, index) => (
        <OfferListLoadingOfferTile
          key={`distance-loading-${index}`}
          tileStyle="search"
          className="offer-tile"
          filters={{ showDistanceWithMetaOfferId: propertyId, checkIn: filters.checkIn, checkOut: filters.checkOut }}
          offerPosition={flashDistanceTile ? index + 1 : index}
        />
      ))
    : distanceOfferList.offerIds
        .filter(id => id !== flashOfferId) // defensive — distance fetch already excludes flash
        .map((offerId, index) => {
          const positionInList = flashDistanceTile ? index + 1 : index
          const shouldHydrateImmediately = positionInList < DISTANCE_PRIORITY_TILE_COUNT

          return <OfferListOfferTile
            key={offerId}
            offerId={offerId}
            position={positionInList}
            eagerLoadImage={positionInList === 0}
            lazyLoad={shouldHydrateImmediately ? 'none' : 'offer-and-inview'}
            tileStyle="search"
            wrapperClass="offer-tile"
            filters={{ showDistanceWithMetaOfferId: propertyId, checkIn: filters.checkIn, checkOut: filters.checkOut }}
            showPerNightPricing={!!shouldShowPerNightPricing}
          />
        })

  const distanceTiles = flashDistanceTile
    ? [flashDistanceTile, ...distanceTilesBelow]
    : distanceTilesBelow

  const shouldShowYmalLoadingTiles = !isDistanceMode &&
    metaSearchOfferList.length === 0 &&
    (
      fetching ||
      metaSearchOfferListFetching
    )

  const ymalTiles = shouldShowYmalLoadingTiles
    ? Array.from({ length: YMAL_PRIORITY_TILE_COUNT }, (_, index) => (
        <OfferListLoadingOfferTile
          key={`ymal-loading-${index}`}
          tileStyle="search"
          className="offer-tile"
          filters={{ showDistanceWithMetaOfferId: propertyId }}
          offerPosition={index}
        />
      ))
    : metaSearchOfferList.map(({ offerId }, index) => {
        const shouldHydrateImmediately = index < YMAL_PRIORITY_TILE_COUNT
        // Recommended YMAL tiles use anytime (non-date-bound) pricing only
        const ymalTileFilters: App.OfferListFilters = { showDistanceWithMetaOfferId: propertyId }

        return (
          <OfferListOfferTile
            key={offerId}
            offerId={offerId}
            position={index}
            eagerLoadImage={index === 0}
            lazyLoad={shouldHydrateImmediately ? 'none' : 'offer-and-inview'}
            tileStyle="search"
            wrapperClass="offer-tile"
            filters={ymalTileFilters}
            showPerNightPricing={!!shouldShowPerNightPricing}
          />
        )
      })

  return <PageWrapper direction="vertical" verticalAlign="space-between" gap={16}>
    <div>
      <MetaSearchContext value={metaSearchOfferList}>
        <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
          <GlobalSearchContextProvider
            state={globalHotelsSearchState}
            dispatch={globalHotelsSearchDispatch}
          >
            { offer && <MetaSearchPageHeadData offer={offer as App.BedbankOffer} searchParams={searchParams} filters={{ ...listFilters }} /> }
            { ymalListKey && <MetaSearchPageWarmupHints ymalListKey={ymalListKey} maxOfferIds={YMAL_PRIORITY_TILE_COUNT} /> }
            <Group direction="vertical" gap={16}>
              {!hideSearchHeader &&
                <HotelSearchHeader
                  filters={filters}
                  formRef={formRef}
                  offerListFiltersProps={offerListFiltersProps}
                  searchVertical="hotels"
                  baseSearchPath={pathName}
                  baseSearchMapPath="/search/map"
                  onFilterChange={handleFilterChange}
                  onSortChange={handleSortChange}
                  showFilters={false}
                />}
              <CSSBreakpoint max="mobile">
                <LayoutContainer>
                  <HorizontalContainerWithFadedOverflow gap={8}>
                    {sortInputs}
                  </HorizontalContainerWithFadedOverflow>
                </LayoutContainer>
              </CSSBreakpoint>
            </Group>
            <VoucherDisplayConfig />
            <StyledLayoutContainer
              size="xl"
              className={clsx({ fullWidth: isMobile })}
            >
              <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
                <StyledVerticalSpacer gap={16}>
                  <CSSBreakpoint min="tablet">
                    <Group direction="horizontal" horizontalAlign="space-between">
                      <HorizontalContainerWithFadedOverflow gap={8}>
                        {sortInputs}
                      </HorizontalContainerWithFadedOverflow>
                      <TextButton
                        kind="secondary"
                        variant="dark"
                        to={mapViewURL}
                        startIcon={<LineMapIcon />}
                      >
                        {mapViewText}
                      </TextButton>
                    </Group>
                  </CSSBreakpoint>
                  <OfferListOfferTile
                    offerId={propertyId}
                    position={0}
                    eagerLoadImage
                    tileStyle="search"
                    wrapperClass="offer-tile"
                    filters={listFilters}
                    isMetaSearch
                  />
                  {shouldShowSaveUpToBanner && (
                    <MessageBanner kind="promotional-primary" icon={<LineInfoCircleIcon />}>
                      {saveUpToBannerText}
                    </MessageBanner>
                  )}
                  {isDistanceMode ? distanceTiles : ymalTiles}
                  {isDistanceMode && !distanceOfferList.streamEnded && distanceMinRequiredOffers < YMAL_LIMIT && (
                    <OfferListLoadMoreButton
                      onClick={onDistanceLoadMore}
                      onScrollNear={onDistanceLoadMore}
                      showLoadingIndicator
                      size="large"
                    >
                      {intl.formatMessage({
                        id: 'NbcY1R5kaZWI1q8j3nio',
                        defaultMessage: 'Load more',
                        description: 'Action button label to load more offers in the offer list',
                      })}
                    </OfferListLoadMoreButton>
                  )}
                </StyledVerticalSpacer>
              </OfferListEventsProvider>
            </StyledLayoutContainer>
          </GlobalSearchContextProvider>
        </AnalyticsPageContext.Provider>
      </MetaSearchContext>
    </div>
    {showFooter && <Footer />}
  </PageWrapper>
}

export default withRouter(connector(MetaSearchPage))
